import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "fileUpload",
    "options",
    "validateButton",
    "importButton",
    "downloadTemplateButton",
    "progressContainer",
    "resultsContainer",
    "errorContainer"
  ]

  static values = {
    importUrl: String,
    templateUrl: String,
    statusUrl: String
  }

  connect() {
    this.currentJobId = null
    this.currentResult = null
    this.isValidated = false
    this.setupEventListeners()
  }

  setupEventListeners() {
    // Listen for file upload events
    if (this.hasFileUploadTarget) {
      this.fileUploadTarget.addEventListener('file-upload:filesSelected', this.handleFilesSelected.bind(this))
      this.fileUploadTarget.addEventListener('file-upload:fileRemoved', this.handleFileRemoved.bind(this))
    }

    // Listen for progress tracker events
    this.element.addEventListener('progress-tracker:completed', this.handleImportCompleted.bind(this))
    this.element.addEventListener('progress-tracker:failed', this.handleImportFailed.bind(this))
    this.element.addEventListener('progress-tracker:cancelled', this.handleImportCancelled.bind(this))
    this.element.addEventListener('progress-tracker:viewResults', this.handleViewResults.bind(this))
    this.element.addEventListener('progress-tracker:retry', this.handleRetryImport.bind(this))

    // Form submission
    if (this.hasFormTarget) {
      this.formTarget.addEventListener('submit', this.handleFormSubmit.bind(this))
    }
  }

  handleFilesSelected(event) {
    const { files, valid } = event.detail
    
    if (valid && files.length > 0) {
      this.enableValidation()
      this.resetResults()
    } else {
      this.disableActions()
    }
  }

  handleFileRemoved() {
    this.disableActions()
    this.resetResults()
  }

  async handleFormSubmit(event) {
    event.preventDefault()
    
    const formData = new FormData(this.formTarget)
    const isValidationOnly = event.submitter?.name === 'validate_only'
    
    // Set validation flag
    formData.set('validate_only', isValidationOnly ? '1' : '0')
    
    try {
      this.showLoading(isValidationOnly ? 'Validating...' : 'Starting import...')
      
      const response = await fetch(this.importUrlValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      const data = await response.json()

      if (data.success) {
        if (isValidationOnly) {
          this.handleValidationResult(data.validation_result)
        } else {
          this.handleImportStarted(data.job_id)
        }
      } else {
        this.handleError(data.errors || ['Import failed'])
      }
    } catch (error) {
      console.error('BulkImportController: Import error:', error)
      this.handleError(['Network error occurred. Please try again.'])
    } finally {
      this.hideLoading()
    }
  }

  async validateFile() {
    const fileUploadController = this.application.getControllerForElementAndIdentifier(
      this.fileUploadTarget, 
      'file-upload'
    )
    
    if (!fileUploadController || !fileUploadController.hasFiles()) {
      this.showError(['Please select a file to validate'])
      return
    }

    // Trigger form submission with validation flag
    const formData = new FormData(this.formTarget)
    formData.set('validate_only', '1')
    
    try {
      this.showLoading('Validating file...')
      
      const response = await fetch(this.importUrlValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      const data = await response.json()

      if (data.success) {
        this.handleValidationResult(data.validation_result)
      } else {
        this.handleError(data.errors || ['Validation failed'])
      }
    } catch (error) {
      console.error('Validation error:', error)
      this.handleError(['Network error occurred during validation'])
    } finally {
      this.hideLoading()
    }
  }

  async startImport() {
    if (!this.isValidated) {
      if (!confirm('File has not been validated. Do you want to proceed?')) {
        return
      }
    }

    const fileUploadController = this.application.getControllerForElementAndIdentifier(
      this.fileUploadTarget, 
      'file-upload'
    )
    
    if (!fileUploadController || !fileUploadController.hasFiles()) {
      this.showError(['Please select a file to import'])
      return
    }

    // Trigger form submission
    const formData = new FormData(this.formTarget)
    formData.set('validate_only', '0')
    
    try {
      this.showLoading('Starting import...')
      
      const response = await fetch(this.importUrlValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      const data = await response.json()

      if (data.success) {
        this.handleImportStarted(data.job_id)
      } else {
        this.handleError(data.errors || ['Import failed to start'])
      }
    } catch (error) {
      console.error('Import start error:', error)
      this.handleError(['Network error occurred while starting import'])
    } finally {
      this.hideLoading()
    }
  }

  async downloadTemplate() {
    try {
      window.open(this.templateUrlValue, '_blank')
    } catch (error) {
      console.error('Template download error:', error)
      this.showError(['Failed to download template'])
    }
  }

  handleValidationResult(result) {
    this.currentResult = result
    this.isValidated = true
    
    if (result.success) {
      this.showValidationSuccess(result)
      this.enableImport()
    } else {
      this.showValidationErrors(result)
      this.disableImport()
    }
  }

  handleImportStarted(jobId) {
    this.currentJobId = jobId
    this.showProgressTracker(jobId)
    this.disableActions()
  }

  handleImportCompleted(event) {
    const { result } = event.detail
    this.currentResult = result
    this.showResults(result)
    this.hideProgressTracker()
    this.enableNewImport()
  }

  handleImportFailed(event) {
    const { result } = event.detail
    this.currentResult = result
    this.showError(result?.errors || ['Import failed'])
    this.hideProgressTracker()
    this.enableRetry()
  }

  handleImportCancelled() {
    this.hideProgressTracker()
    this.enableNewImport()
    this.showInfo(['Import was cancelled'])
  }

  handleViewResults() {
    if (this.currentResult) {
      this.showResults(this.currentResult)
    }
  }

  handleRetryImport() {
    this.resetState()
    this.enableValidation()
  }

  showValidationSuccess(result) {
    const message = `Validation successful! Found ${result.total_rows} rows with ${result.valid_rows} valid and ${result.invalid_rows} invalid.`
    this.showSuccess([message])
    
    if (result.invalid_rows > 0) {
      this.showValidationErrors(result)
    }
  }

  showValidationErrors(result) {
    if (result.error_details && result.error_details.length > 0) {
      const errors = result.error_details.map(error => 
        `Row ${error.row}: ${error.errors.join(', ')}`
      )
      this.showError(errors)
    } else {
      if (result.errors && result.errors.length > 0) {
        this.showError(result.errors)
      } else {
        this.showError(['Validation failed with unknown errors'])
      }
    }
  }

  showProgressTracker(jobId) {
    if (this.hasProgressContainerTarget) {
      // Create progress tracker dynamically
      const progressHTML = this.createProgressTrackerHTML(jobId)
      this.progressContainerTarget.innerHTML = progressHTML
      this.progressContainerTarget.classList.remove('hidden')
    }
  }

  hideProgressTracker() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add('hidden')
    }
  }

  showResults(result) {
    if (this.hasResultsContainerTarget) {
      const resultsHTML = this.createResultsHTML(result)
      this.resultsContainerTarget.innerHTML = resultsHTML
      this.resultsContainerTarget.classList.remove('hidden')
    }
  }

  hideResults() {
    if (this.hasResultsContainerTarget) {
      this.resultsContainerTarget.classList.add('hidden')
    }
  }

  showError(errors) {
    this.hideSuccess()
    this.hideInfo()
    
    if (this.hasErrorContainerTarget) {
      const errorList = Array.isArray(errors) ? errors : [errors]
      const errorHTML = this.createErrorHTML(errorList)
      this.errorContainerTarget.innerHTML = errorHTML
      this.errorContainerTarget.classList.remove('hidden')
    }
  }

  showSuccess(messages) {
    this.hideError()
    this.hideInfo()
    
    // Find or create success container
    const successContainer = this.element.querySelector('.success-messages') || this.createSuccessContainer()
    const messageList = Array.isArray(messages) ? messages : [messages]
    const successHTML = this.createSuccessHTML(messageList)
    successContainer.innerHTML = successHTML
    successContainer.classList.remove('hidden')
  }

  showInfo(messages) {
    this.hideError()
    this.hideSuccess()
    
    // Find or create info container
    const infoContainer = this.element.querySelector('.info-messages') || this.createInfoContainer()
    const messageList = Array.isArray(messages) ? messages : [messages]
    const infoHTML = this.createInfoHTML(messageList)
    infoContainer.innerHTML = infoHTML
    infoContainer.classList.remove('hidden')
  }

  showLoading(message = 'Processing...') {
    // Add loading state to buttons
    this.disableActions()
    
    // Show loading message
    this.showInfo([message])
  }

  hideLoading() {
    this.hideInfo()
  }

  hideError() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.add('hidden')
    }
  }

  hideSuccess() {
    const successContainer = this.element.querySelector('.success-messages')
    if (successContainer) {
      successContainer.classList.add('hidden')
    }
  }

  hideInfo() {
    const infoContainer = this.element.querySelector('.info-messages')
    if (infoContainer) {
      infoContainer.classList.add('hidden')
    }
  }

  enableValidation() {
    if (this.hasValidateButtonTarget) {
      this.validateButtonTarget.disabled = false
      this.validateButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  enableImport() {
    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = false
      this.importButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  enableRetry() {
    this.enableValidation()
    if (this.isValidated) {
      this.enableImport()
    }
  }

  enableNewImport() {
    this.enableValidation()
    this.enableImport()
  }

  disableActions() {
    if (this.hasValidateButtonTarget) {
      this.validateButtonTarget.disabled = true
      this.validateButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
    
    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = true
      this.importButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  disableImport() {
    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = true
      this.importButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  resetState() {
    this.currentJobId = null
    this.currentResult = null
    this.isValidated = false
    this.hideProgressTracker()
    this.hideResults()
    this.hideError()
    this.hideSuccess()
    this.hideInfo()
  }

  resetResults() {
    this.hideResults()
    this.hideError()
    this.hideSuccess()
    this.hideInfo()
    this.isValidated = false
  }

  // HTML generation methods
  createProgressTrackerHTML(jobId) {
    return `
      <div data-controller="progress-tracker" 
           data-progress-tracker-job-id-value="${jobId}"
           data-progress-tracker-auto-refresh-value="true"
           data-progress-tracker-refresh-interval-value="1000"
           data-progress-tracker-status-url-value="${this.statusUrlValue}">
        <!-- Progress tracker content will be rendered by the component -->
      </div>
    `
  }

  createResultsHTML(result) {
    // This would typically render the ImportResultsComponent
    // For now, return a simple representation
    return `
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Import Results</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="text-center">
            <div class="text-2xl font-bold text-gray-900">${result.total_rows || 0}</div>
            <div class="text-sm text-gray-600">Total</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-green-600">${result.created_count || 0}</div>
            <div class="text-sm text-gray-600">Created</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-blue-600">${result.updated_count || 0}</div>
            <div class="text-sm text-gray-600">Updated</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-red-600">${result.invalid_rows || 0}</div>
            <div class="text-sm text-gray-600">Errors</div>
          </div>
        </div>
      </div>
    `
  }

  createErrorHTML(errors) {
    const totalErrors = errors.length
    const maxPreviewErrors = 5
    const hasMoreErrors = totalErrors > maxPreviewErrors
    const previewErrors = errors.slice(0, maxPreviewErrors)
    const remainingErrors = errors.slice(maxPreviewErrors)
    
    const previewErrorList = previewErrors.map(error => `<li class="text-sm">${error}</li>`).join('')
    const allErrorList = errors.map(error => `<li class="text-sm">${error}</li>`).join('')
    
    return `
      <div class="bg-red-50 border border-red-200 rounded-lg shadow-sm">
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div class="ml-3 flex-1">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold text-red-800">
                  Validation Failed
                </h3>
                <div class="flex items-center space-x-2">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    ${totalErrors} error${totalErrors !== 1 ? 's' : ''}
                  </span>
                  ${hasMoreErrors ? `
                    <button type="button" class="inline-flex items-center text-xs font-medium text-red-700 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-md px-2 py-1" onclick="this.closest('.bg-red-50').querySelector('.error-preview').classList.add('hidden'); this.closest('.bg-red-50').querySelector('.error-full').classList.remove('hidden'); this.classList.add('hidden'); this.closest('.bg-red-50').querySelector('.collapse-btn').classList.remove('hidden');">
                      <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                      Show all errors
                    </button>
                    <button type="button" class="collapse-btn hidden inline-flex items-center text-xs font-medium text-red-700 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-md px-2 py-1" onclick="this.closest('.bg-red-50').querySelector('.error-preview').classList.remove('hidden'); this.closest('.bg-red-50').querySelector('.error-full').classList.add('hidden'); this.classList.add('hidden'); this.closest('.bg-red-50').querySelector('button').classList.remove('hidden');">
                      <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                      </svg>
                      Show less
                    </button>
                  ` : ''}
                </div>
              </div>
              
              <!-- Error Preview (first 5 errors) -->
              <div class="error-preview mt-2">
                <div class="text-sm text-red-700">
                  <ul class="space-y-1">
                    ${previewErrorList}
                  </ul>
                  ${hasMoreErrors ? `
                    <div class="mt-2 text-xs text-red-600 italic">
                      and ${remainingErrors.length} more error${remainingErrors.length !== 1 ? 's' : ''}...
                    </div>
                  ` : ''}
                </div>
              </div>
              
              <!-- Full Error List (scrollable) -->
              <div class="error-full hidden mt-2">
                <div class="max-h-64 overflow-y-auto border border-red-200 rounded-md bg-white p-3">
                  <div class="text-sm text-red-700">
                    <ul class="space-y-1">
                      ${allErrorList}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }

  createSuccessHTML(messages) {
    const messageList = messages.map(message => `<li class="text-sm">${message}</li>`).join('')
    return `
      <div class="bg-green-50 border border-green-200 rounded-lg shadow-sm">
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="ml-3 flex-1">
              <h3 class="text-sm font-semibold text-green-800">Success</h3>
              <div class="mt-2 text-sm text-green-700">
                <ul class="space-y-1">
                  ${messageList}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }

  createInfoHTML(messages) {
    const messageList = messages.map(message => `<li class="text-sm">${message}</li>`).join('')
    return `
      <div class="bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="ml-3 flex-1">
              <h3 class="text-sm font-semibold text-blue-800">Information</h3>
              <div class="mt-2 text-sm text-blue-700">
                <ul class="space-y-1">
                  ${messageList}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }

  createSuccessContainer() {
    const container = document.createElement('div')
    container.className = 'success-messages mt-4 hidden'
    this.element.appendChild(container)
    return container
  }

  createInfoContainer() {
    const container = document.createElement('div')
    container.className = 'info-messages mt-4 hidden'
    this.element.appendChild(container)
    return container
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }
} 